import { type PropsWithChildren } from 'react';

// Eval: use Light / AsyncLight ?
import { Prism as SyntaxHighlighter, SyntaxHighlighterProps } from 'react-syntax-highlighter';

import varnishPrismLight from '@allenai/varnish-theme/varnishPrismLight';
import varnishPrismDark from '@allenai/varnish-theme/varnishPrismDark';

import { cx } from '@/utils/cx';
import type { RemoveIndex } from '@/utils/types';

import codeBlockRecipe, { CodeBlockRecipeProps } from './codeblock.styles';

// remove the index prop: `[spread: string]: any` -- fixes typing for Markdown
// Remove `style` and `children` props, as they are drastically different
type SyntaxHighlighterPropsBase = Omit<RemoveIndex<SyntaxHighlighterProps>, 'style' | 'children'>;

type RequireChildrenOrValue =
    | { value: string; children?: never }
    | PropsWithChildren<{ value?: never }>;

type CodeBlockProps = {
    language?: SyntaxHighlighterProps['language'];
    PreTag?: SyntaxHighlighterProps['PreTag'];
    CodeTag?: SyntaxHighlighterProps['CodeTag'];
    inline?: boolean;
    className?: string;
    codeTagClassName?: string;
    // style?: CSSProperties; // uncommenting this breaks types?
    codeStyle?: SyntaxHighlighterProps['style']; // why they named this the same as a React prop, I don't know
    customStyle?: SyntaxHighlighterProps['customStyle'];
    colorMode?: 'light' | 'dark';
} & CodeBlockRecipeProps &
    RequireChildrenOrValue &
    SyntaxHighlighterPropsBase;

const CodeBlock = ({
    language,
    PreTag = 'pre',
    CodeTag = 'code',
    codeStyle,
    customStyle,
    colorMode = 'light',
    wrapLongLines = true,
    value, // should we remove `value`, we are passing to children (from Markdown for example) anyways
    codeTagClassName,
    className, // outter container (`PreTag`) className
    inline = false,
    children,
    ...rest
}: CodeBlockProps) => {
    const recipeClassNames = codeBlockRecipe({ colorMode });

    const valueOrChildren = value ?? children;

    const languageDerived = language ?? className?.replace(/\blanguage-([^\s]+)/, '$1');

    const prismTheme =
        codeStyle ||
        ((colorMode === 'dark'
            ? varnishPrismDark
            : varnishPrismLight) as SyntaxHighlighterProps['style']);

    const inlineStyles = inline
        ? { display: 'inline', padding: 0, whiteSpace: 'nowrap' }
        : undefined;

    if (typeof valueOrChildren === 'string') {
        return (
            <SyntaxHighlighter
                language={languageDerived}
                PreTag={PreTag}
                CodeTag={CodeTag}
                style={prismTheme}
                codeTagProps={{
                    className: cx(recipeClassNames.code, codeTagClassName),
                }}
                wrapLongLines={wrapLongLines}
                className={cx(recipeClassNames.root, className)}
                customStyle={{
                    ...inlineStyles,
                    ...customStyle,
                }}
                {...rest}>
                {valueOrChildren}
            </SyntaxHighlighter>
        );
    }

    // Do something if we don't get a string
    return (
        <PreTag className={className}>
            <CodeTag className={codeTagClassName}>{valueOrChildren}</CodeTag>
        </PreTag>
    );
};

export { CodeBlock };
export type { CodeBlockProps };
